import { createApp } from 'vue'
import '@zoomcatalog/design-system/dist/main.css'
import '@zoomcatalog/core-ui/dist/main.css'
import '@zoomcatalog/design-templates/dist/index.css'
import '@zoomcatalog/auth/dist/index.css'
import '@/scss/styles.scss'
import { VueQueryPlugin } from '@tanstack/vue-query'
import { useSnifferServices } from '@zoomcatalog/composable'
import App from './App.vue'
import api, { setBaseUrl } from './config/axios'
import { setupSentry } from './config/sentry'
import {
	createSmartLayoutsI18n,
	setLanguage,
	SUPPORTED_LOCALES,
	DEFAULT_LOCALE,
} from './i18n'
import type { SupportedLocale } from './i18n'
import router from './router'
import { ENV, settings } from './settings/DynamicSettings'
import { rootStore, useCommit } from './store/rootStore'

const app = createApp(App)

// Add global error handler for better debugging
app.config.errorHandler = (err, instance, info) => {
	const error = err as Error | null | undefined
	// eslint-disable-next-line no-console
	console.error('Vue Error Handler:', {
		error: err,
		message: error?.message,
		stack: error?.stack,
		instance,
		info,
		component: instance?.$options?.name || instance?.$options?.__name,
	})

	if (error?.stack?.includes('renderSlot')) {
		// eslint-disable-next-line no-console
		console.error('renderSlot Error - Component details:', {
			component: instance?.$options,
			props: instance?.$props,
			data: instance?.$data,
		})
	}
}

const i18n = createSmartLayoutsI18n()

const LANGUAGE_STORAGE_KEY = 'zoomcatalog-language'

function getInitialLanguage(): SupportedLocale {
	const urlParams = new URLSearchParams(window.location.search)
	const langParam = urlParams.get('lang')

	if (langParam && SUPPORTED_LOCALES.includes(langParam as SupportedLocale)) {
		sessionStorage.setItem(LANGUAGE_STORAGE_KEY, langParam)
		return langParam as SupportedLocale
	}

	const savedLanguage = sessionStorage.getItem(LANGUAGE_STORAGE_KEY)
	if (
		savedLanguage &&
		SUPPORTED_LOCALES.includes(savedLanguage as SupportedLocale)
	) {
		return savedLanguage as SupportedLocale
	}

	return DEFAULT_LOCALE
}

// Main initialization function
async function initializeApp() {
	const initialLanguage = getInitialLanguage()

	setLanguage(initialLanguage)

	try {
		const { createZoomI18n, setLanguage: setSharedLanguage } = await import(
			'@zoomcatalog/shared'
		)

		// Initialize shared i18n with the correct language from the start
		createZoomI18n({ locale: initialLanguage })

		// Set the language (in case it wasn't set during creation)
		setSharedLanguage(initialLanguage)
		// eslint-disable-next-line no-console
		console.log(`Language set to: ${initialLanguage}`)
	} catch (error) {
		// eslint-disable-next-line no-console
		console.warn('Could not set language for design system:', error)
	}

	await setupApp(app)
	app.use(router).use(rootStore).use(i18n).use(VueQueryPlugin).mount('#app')
}

// Start the app
initializeApp()

async function setupApp(app: ReturnType<typeof createApp>) {
	setupSentry(app)
	setupAnalytics()

	if (settings.ENABLE_MSW) {
		const { worker } = await import('./mocks/browser')
		worker.start({ onUnhandledRequest: 'bypass' })
		setBaseUrl(`${window.origin}/api`)
	} else {
		await setupSniffer()
	}
}

function setupAnalytics() {
	if (import.meta.env.VITE_USE_ANALYTICS === 'true') {
		window.analytics.load(import.meta.env.VITE_SEGMENT_KEY as string, {
			integrations: {
				Sentry: false,
			},
		})
	}
}

async function setupSniffer() {
	const commit = useCommit()

	const environment = import.meta.env.MODE as ENV
	if (environment !== 'development') {
		const snifferService = await useSnifferServices({
			useDefaultEndpoints: true,
			httpGetRequest: api.get,
			snifferQuery: {
				environment: environment === 'production' ? 'prod' : environment,
				servicesList: [
					'companies',
					'assets',
					'helpers',
					'quickcanvas',
					'users',
					'auth',
					'sso',
					'sso2',
					'virtual-sample',
				],
			},
		})
		commit({
			type: 'sniffer/setServicesEndpoints',
			payload: snifferService.baseUrls,
		})

		setBaseUrl(`${snifferService.baseUrls.QC_API_ENDPOINT}canvas/v1`)
	}
	// else {
	// 	// Development environment - use environment variables directly
	// 	const developmentEndpoints = {
	// 		QC_API_ENDPOINT: import.meta.env.VITE_API_ENDPOINT,
	// 		USERS_API_ENDPOINT: import.meta.env.VITE_USERS_API_ENDPOINT,
	// 		COMPANIES_API_ENDPOINT: import.meta.env.VITE_COMPANIES_API_ENDPOINT,
	// 		ASSETS_API_ENDPOINT: import.meta.env.VITE_ASSETS_API_ENDPOINT,
	// 		HELPERS_API_ENDPOINT: import.meta.env.VITE_HELPERS_API_ENDPOINT,
	// 		SSO2_API_ENDPOINT: import.meta.env.VITE_SSO2_API_ENDPOINT,
	// 	}

	// 	commit({
	// 		type: 'sniffer/setServicesEndpoints',
	// 		payload: developmentEndpoints,
	// 	})

	// 	setBaseUrl(`${developmentEndpoints.QC_API_ENDPOINT}canvas/v1`)
	// }
}
